import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const SAMPLES = [
  'https://images.unsplash.com/photo-1518972559570-7cc1309f3229?w=320&h=320&fit=crop',
  'https://images.unsplash.com/photo-1533106497176-45ae19e68ba2?w=320&h=320&fit=crop',
  'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=320&h=320&fit=crop',
  'https://images.unsplash.com/photo-1527525443983-6e60c75fff46?w=320&h=320&fit=crop',
  'https://images.unsplash.com/photo-1504609773096-104ff2c73ba4?w=320&h=320&fit=crop',
  'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=320&h=320&fit=crop',
];

const S = ({ i, className = '' }) => (
  <img loading="lazy" decoding="async" src={SAMPLES[i % SAMPLES.length]} alt="" className={`object-cover ${className}`} />
);

// Mini-preview per theme — small inside each card.
const previews = {
  grid: (
    <div className="grid grid-cols-3 gap-1 w-full h-full">
      {[0,1,2,3,4,5].map((i)=>(<S key={i} i={i} className="w-full h-full rounded-sm" />))}
    </div>
  ),
  masonry: (
    <div className="columns-3 gap-1 w-full">
      <S i={0} className="w-full h-10 mb-1 rounded-sm" />
      <S i={1} className="w-full h-16 mb-1 rounded-sm" />
      <S i={2} className="w-full h-12 mb-1 rounded-sm" />
      <S i={3} className="w-full h-8 rounded-sm" />
      <S i={4} className="w-full h-14 rounded-sm" />
      <S i={5} className="w-full h-10 rounded-sm" />
    </div>
  ),
  polaroid: (
    <div className="relative w-full h-full flex items-center justify-center">
      <div className="bg-white p-1 pb-3 shadow-lg absolute" style={{ transform: 'rotate(-6deg)' }}><S i={0} className="w-12 h-12" /></div>
      <div className="bg-white p-1 pb-3 shadow-lg absolute" style={{ transform: 'rotate(4deg) translate(10px, 4px)' }}><S i={1} className="w-12 h-12" /></div>
      <div className="bg-white p-1 pb-3 shadow-lg absolute" style={{ transform: 'rotate(-2deg) translate(-14px, 10px)' }}><S i={2} className="w-12 h-12" /></div>
    </div>
  ),
  slideshow: (
    <div className="relative w-full h-full rounded overflow-hidden">
      <S i={2} className="w-full h-full" />
      <div className="absolute bottom-1 left-0 right-0 flex justify-center gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-white" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/40" />
      </div>
    </div>
  ),
  kenburns: (
    <div className="relative w-full h-full rounded overflow-hidden">
      <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 4, repeat: Infinity }} className="w-full h-full"><S i={4} className="w-full h-full" /></motion.div>
    </div>
  ),
  carousel: (
    <div className="flex gap-1 w-full items-center overflow-hidden">
      <S i={0} className="h-14 w-10 rounded-sm shrink-0 opacity-60" />
      <S i={1} className="h-16 w-14 rounded-sm shrink-0" />
      <S i={2} className="h-14 w-10 rounded-sm shrink-0 opacity-60" />
    </div>
  ),
  mosaic: (
    <div className="grid grid-cols-4 grid-rows-3 gap-1 w-full h-full">
      <S i={0} className="col-span-2 row-span-2 w-full h-full rounded-sm" />
      <S i={1} className="col-span-2 w-full h-full rounded-sm" />
      <S i={2} className="w-full h-full rounded-sm" />
      <S i={3} className="w-full h-full rounded-sm" />
      <S i={4} className="col-span-2 w-full h-full rounded-sm" />
    </div>
  ),
  justified: (
    <div className="flex flex-wrap gap-1 w-full">
      <S i={0} className="h-8 flex-grow basis-14 rounded-sm" />
      <S i={1} className="h-8 flex-grow basis-20 rounded-sm" />
      <S i={2} className="h-8 flex-grow basis-16 rounded-sm" />
      <S i={3} className="h-8 flex-grow basis-24 rounded-sm" />
      <S i={4} className="h-8 flex-grow basis-14 rounded-sm" />
    </div>
  ),
  timeline: (
    <div className="relative pl-3 border-l-2 border-primary/70 h-full flex flex-col justify-between py-1">
      <div className="relative"><div className="absolute -left-[14px] top-1 w-2 h-2 rounded-full bg-primary" /><S i={0} className="w-16 h-6 rounded-sm" /></div>
      <div className="relative"><div className="absolute -left-[14px] top-1 w-2 h-2 rounded-full bg-primary" /><S i={1} className="w-20 h-6 rounded-sm" /></div>
      <div className="relative"><div className="absolute -left-[14px] top-1 w-2 h-2 rounded-full bg-primary" /><S i={2} className="w-14 h-6 rounded-sm" /></div>
    </div>
  ),
  filmstrip: (
    <div className="flex gap-1 bg-stone-800 p-1 rounded w-full items-center">
      <S i={0} className="h-10 w-12 rounded-sm" />
      <S i={1} className="h-10 w-12 rounded-sm" />
      <S i={2} className="h-10 w-12 rounded-sm" />
    </div>
  ),
  spotlight: (
    <div className="w-full h-full flex flex-col gap-1.5">
      <div className="relative w-full flex-1 rounded-sm overflow-hidden">
        <S i={1} className="w-full h-full" />
        <div className="absolute bottom-0.5 left-1 text-[8px] text-white/90 font-bold bg-black/50 px-1 rounded">HERO</div>
      </div>
      <div className="flex gap-1 justify-center">
        <S i={0} className="w-7 h-6 rounded-sm opacity-70" />
        <div className="w-7 h-6 rounded-sm ring-2 ring-primary overflow-hidden"><S i={1} className="w-full h-full" /></div>
        <S i={2} className="w-7 h-6 rounded-sm opacity-70" />
        <S i={3} className="w-7 h-6 rounded-sm opacity-70" />
      </div>
    </div>
  ),
  scrapbook: null, // reuses polaroid
  cube3d: (
    <div className="w-full h-full flex items-center justify-center" style={{ perspective: 300 }}>
      <motion.div style={{ transformStyle: 'preserve-3d', width: 50, height: 50 }} animate={{ rotateY: 360 }} transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}>
        <div className="absolute inset-0 overflow-hidden" style={{ transform: 'rotateY(0deg) translateZ(25px)' }}><S i={0} className="w-full h-full" /></div>
        <div className="absolute inset-0 overflow-hidden" style={{ transform: 'rotateY(90deg) translateZ(25px)' }}><S i={1} className="w-full h-full" /></div>
        <div className="absolute inset-0 overflow-hidden" style={{ transform: 'rotateY(180deg) translateZ(25px)' }}><S i={2} className="w-full h-full" /></div>
        <div className="absolute inset-0 overflow-hidden" style={{ transform: 'rotateY(-90deg) translateZ(25px)' }}><S i={3} className="w-full h-full" /></div>
      </motion.div>
    </div>
  ),
  coverflow: (
    <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: 400 }}>
      <S i={0} className="h-10 w-14 absolute rounded-sm" style={{ transform: 'translateX(-28px) rotateY(35deg)' }} />
      <S i={1} className="h-14 w-20 absolute rounded-sm z-10" />
      <S i={2} className="h-10 w-14 absolute rounded-sm" style={{ transform: 'translateX(28px) rotateY(-35deg)' }} />
    </div>
  ),
  flipcards: (
    <div className="grid grid-cols-3 gap-1 w-full">
      <motion.div animate={{ rotateY: [0, 180, 0] }} transition={{ duration: 4, repeat: Infinity }} style={{ transformStyle: 'preserve-3d' }}>
        <S i={0} className="w-full h-10 rounded-sm" />
      </motion.div>
      <S i={1} className="w-full h-10 rounded-sm" />
      <S i={2} className="w-full h-10 rounded-sm" />
    </div>
  ),
  marquee: (
    <div className="overflow-hidden w-full">
      <motion.div className="flex gap-1" animate={{ x: ['0%', '-50%'] }} transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}>
        {[0,1,2,3,0,1,2,3].map((i, idx) => (<S key={idx} i={i} className="h-14 w-16 rounded-sm shrink-0" />))}
      </motion.div>
    </div>
  ),
  kinetic: (
    <div className="w-full h-full flex items-center justify-center gap-1">
      {[0,1,2].map((i) => (
        <motion.div key={i} animate={{ y: [0, -8, 0] }} transition={{ duration: 2, repeat: Infinity, delay: i * 0.3 }}>
          <S i={i} className="w-10 h-10 rounded-full" />
        </motion.div>
      ))}
    </div>
  ),
  parallax: (
    <div className="relative w-full h-full overflow-hidden rounded">
      <S i={5} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
      <div className="absolute bottom-1 left-1 right-1 h-2 bg-white/30 rounded-sm" />
    </div>
  ),
  immersive: null, // reuses parallax
  skyscraper: (
    <div className="relative w-full h-full flex items-end justify-center">
      {/* Antenna */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 flex flex-col items-center">
        <div className="w-[2px] h-3 bg-stone-700" />
        <div className="w-[6px] h-[6px] rounded-full bg-amber-400 shadow-[0_0_6px_rgba(251,191,36,0.9)]" />
      </div>
      {/* Building */}
      <div className="relative bg-stone-900 border-x-[6px] border-stone-800 w-[72px] mt-5 pt-1 pb-1 px-1">
        <div className="grid grid-cols-2 gap-[3px]">
          {[0,1,2,3,4,5,0,1].map((i, idx) => (
            <div key={idx} className="aspect-[3/4] overflow-hidden ring-1 ring-stone-800">
              <S i={i} className="w-full h-full brightness-110" />
            </div>
          ))}
        </div>
        {/* Entrance */}
        <div className="mx-auto mt-1 w-5 h-3 bg-amber-500/70 rounded-t-md" />
      </div>
      {/* Street */}
      <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-stone-700" />
    </div>
  ),
  vinyl: (
    <div className="relative w-full h-full flex items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
        className="relative w-20 h-20 rounded-full shadow-xl"
        style={{
          background:
            'radial-gradient(circle at center, #111 36%, #1b1b1b 37%, #0a0a0a 42%, #1a1a1a 43%, #0a0a0a 55%, #1a1a1a 56%, #0a0a0a 65%, #1a1a1a 66%, #0a0a0a 78%, #141414 100%)',
        }}
      >
        <div className="absolute inset-[32%] rounded-full overflow-hidden ring-2 ring-stone-900">
          <S i={2} className="w-full h-full" />
        </div>
        <div className="absolute inset-[48%] rounded-full bg-stone-900 ring-1 ring-stone-700" />
      </motion.div>
    </div>
  ),
  carousel3d: (
    <div className="relative w-full h-full flex items-center justify-center" style={{ perspective: 500 }}>
      <div style={{ transformStyle: 'preserve-3d', transform: 'rotateX(-28deg)' }}>
        <motion.div
          className="relative"
          style={{ width: 60, height: 60, transformStyle: 'preserve-3d' }}
          animate={{ rotateY: 360 }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        >
          {[0,1,2,3,4,5].map((i, idx) => (
            <div
              key={idx}
              className="absolute top-0 left-0 w-[60px] h-[60px] overflow-hidden rounded"
              style={{ transform: `rotateY(${idx * 60}deg) translateZ(55px)` }}
            >
              <S i={i} className="w-full h-full" />
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  ),
  filmreel: (
    <div className="relative w-full h-full flex items-center justify-center gap-1">
      {/* Left reel */}
      <motion.div
        className="relative w-10 h-10 rounded-full bg-stone-900 ring-2 ring-stone-700 shrink-0"
        animate={{ rotate: 360 }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
      >
        <div className="absolute inset-[20%] rounded-full bg-stone-950 ring-1 ring-stone-800" />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%)' }} />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%) rotate(60deg)' }} />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%) rotate(120deg)' }} />
      </motion.div>
      {/* Film strip */}
      <div className="relative flex-1 bg-stone-950 overflow-hidden py-1">
        <motion.div
          className="flex gap-[2px]"
          animate={{ x: ['0%', '-50%'] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        >
          {[0,1,2,3,4,5,0,1,2,3,4,5].map((i, idx) => (
            <div key={idx} className="relative shrink-0">
              <div className="absolute -top-0.5 inset-x-0 h-1 flex justify-around items-center">
                <div className="w-0.5 h-0.5 bg-stone-950 ring-[0.5px] ring-stone-800" />
                <div className="w-0.5 h-0.5 bg-stone-950 ring-[0.5px] ring-stone-800" />
              </div>
              <S i={i} className="w-10 h-6 ring-1 ring-stone-800" />
              <div className="absolute -bottom-0.5 inset-x-0 h-1 flex justify-around items-center">
                <div className="w-0.5 h-0.5 bg-stone-950 ring-[0.5px] ring-stone-800" />
                <div className="w-0.5 h-0.5 bg-stone-950 ring-[0.5px] ring-stone-800" />
              </div>
            </div>
          ))}
        </motion.div>
      </div>
      {/* Right reel */}
      <motion.div
        className="relative w-10 h-10 rounded-full bg-stone-900 ring-2 ring-stone-700 shrink-0"
        animate={{ rotate: -360 }}
        transition={{ duration: 6, repeat: Infinity, ease: 'linear' }}
      >
        <div className="absolute inset-[20%] rounded-full bg-stone-950 ring-1 ring-stone-800" />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%)' }} />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%) rotate(60deg)' }} />
        <div className="absolute left-1/2 top-1/2 w-[2px] h-[90%] bg-stone-700" style={{ transform: 'translate(-50%, -50%) rotate(120deg)' }} />
      </motion.div>
    </div>
  ),
  videowall: (
    <div className="grid grid-cols-3 gap-1 w-full h-full">
      {[0,1,2,3,4,5].map((i) => (
        <div key={i} className="relative overflow-hidden rounded-sm">
          <S i={i} className="w-full h-full" />
          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
            <div className="w-0 h-0 border-y-[5px] border-y-transparent border-l-[8px] border-l-white" />
          </div>
          <div className="absolute top-0.5 left-0.5 px-1 py-[1px] text-[7px] bg-red-600 text-white font-bold rounded">VID</div>
        </div>
      ))}
    </div>
  ),
};

const DESCRIPTIONS = {
  grid: 'Clean equal squares',
  masonry: 'Pinterest-style',
  polaroid: 'Tilted photo stack',
  slideshow: 'One-at-a-time',
  kenburns: 'Slow zoom slideshow',
  carousel: 'Horizontal scroll',
  mosaic: 'Varied sizes',
  justified: 'Flexible rows',
  timeline: 'Chronological trail',
  filmstrip: 'Horizontal strip',
  spotlight: 'Big photo + thumbnail row',
  scrapbook: 'Scattered polaroids',
  cube3d: '3D rotating cube',
  coverflow: '3D flip-through',
  flipcards: 'Hover-flip reveal',
  marquee: 'Scrolling ribbon',
  kinetic: 'Floating circles',
  parallax: 'Fullscreen scroll',
  immersive: 'One-per-screen story',
  skyscraper: 'Images as lit windows',
  vinyl: 'Spinning record covers',
  carousel3d: 'Tilted merry-go-round',
  filmreel: 'Rotating reels + film strip',
  videowall: 'Grid of videos (hover to play)',
};

export const THEME_META = Object.keys(previews);

// Themes designed with video playback as a first-class citizen.
const VIDEO_FIRST = new Set(['videowall', 'filmreel', 'slideshow', 'kenburns', 'spotlight', 'carousel', 'filmstrip', 'parallax', 'immersive']);

const DISPLAY_NAMES = {
  cube3d: '3D Cube',
  carousel3d: '3D Carousel',
  videowall: 'Video Wall',
  filmreel: 'Film Reel',
};

export const ThemePicker = ({ value, onChange }) => {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gc-theme-picker">
      {THEME_META.map((key) => {
        const selected = value === key;
        const Preview = previews[key] || previews.polaroid;
        const videoReady = VIDEO_FIRST.has(key);
        const displayName = DISPLAY_NAMES[key] || key;
        return (
          <button
            type="button"
            key={key}
            onClick={() => onChange(key)}
            className={`relative group text-left rounded-xl overflow-hidden border-2 transition-colors ${
              selected ? 'border-primary' : 'border-stone-800 hover:border-stone-600'
            }`}
            data-testid={`gc-theme-${key}`}
          >
            <div className="aspect-square bg-stone-900 p-2 flex items-center justify-center overflow-hidden">
              {Preview}
            </div>
            <div className="px-3 py-2 bg-stone-900">
              <div className="text-sm font-bold capitalize flex items-center gap-1.5">
                <span>{displayName}</span>
                {videoReady && (
                  <span className="px-1.5 py-[1px] text-[9px] bg-red-600/90 text-white font-bold rounded uppercase tracking-wider">
                    Video
                  </span>
                )}
              </div>
              <div className="text-xs text-stone-400">{DESCRIPTIONS[key]}</div>
            </div>
            {selected && (
              <div className="absolute top-2 right-2 w-6 h-6 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg">
                <Check className="w-4 h-4" />
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ThemePicker;
